import type { CompanyItemType } from "@/modules/companies/client/components/CompanyItem/CompanyItem"

import classNames from "classnames"
import { type FunctionComponent, useState } from "react"

import { Heading } from "@jobteaser/spark/components/Heading"
import { Button, BUTTON_VARIANT } from "@jobteaser/ui-kit/components/Button"
import { Layout, LAYOUT_COLOR_BLOCK } from "@jobteaser/ui-kit/dist/module/components/Layout/Layout"

import { CompanyItem } from "@/modules/companies/client/components/CompanyItem/CompanyItem"
import { useCompanyTrack } from "@/modules/companies/client/hooks/tracking/useCompanyTrack"
import { useCompanyFetch } from "@/modules/companies/client/hooks/useCompanyFetch"
import { useTranslation } from "@/modules/i18n/components/useTranslation"

import styles from "./CompanyEcosystem.module.css"

const MAX_COMPANY_ITEMS_DISPLAYED = 8

export const CompanyEcosystem: FunctionComponent = () => {
  const companyTrack = useCompanyTrack()
  const { t } = useTranslation(["companies_client"])
  const [isDisplayAllCompanies, setIsDisplayAllCompanies] = useState(false)
  const { dataCompany } = useCompanyFetch()

  if (!dataCompany) return null

  const {
    id,
    pageType,
    page: { name, description, logoUrl, associatedCompanies, brands },
  } = dataCompany

  const premiumCompany = pageType === "COMPANY_PAGE_TYPE_PREMIUM"

  if (
    (associatedCompanies.length === 0 ||
      associatedCompanies.filter(associatedCompany => associatedCompany.companyPage).length === 0) &&
    brands.length === 0
  ) {
    return null
  }

  const displayMore = (): void => {
    setIsDisplayAllCompanies(prev => !prev)
    companyTrack("see_more_information_click", { company_id: id, company_name: name, context: "brands_and_companies" })
  }

  const createCompanyItem = (
    companyName: string,
    companyDescription: string | null,
    companyUrl: string,
    companyPermalink: string | null
    // eslint-disable-next-line max-params
  ): CompanyItemType => {
    return {
      description: companyDescription,
      logoUrl: companyUrl,
      name: companyName,
      permalink: companyPermalink,
    }
  }

  const currentCompanyItem = { description, logoUrl, name, permalink: null }
  const currentCompanyHasParent = !!associatedCompanies.find(({ isParent }) => isParent)

  const companyParent: CompanyItemType =
    associatedCompanies
      .filter(company => company.isParent)
      .map(company => createCompanyItem(company.name, company.description, company.logoUrl, company.permalink))[0] ||
    currentCompanyItem

  const companyItems: CompanyItemType[] = [
    ...associatedCompanies
      .filter(company => !company.isParent && company.companyPage)
      .map(company => createCompanyItem(company.name, company.description, company.logoUrl, company.permalink)),
  ]

  if (currentCompanyHasParent) {
    companyItems.push(currentCompanyItem)
  }

  if (premiumCompany) {
    companyItems.push(...brands.map(brand => createCompanyItem(brand.title, null, brand.logo.url, null)))
  }

  const hasMoreCompanyItems = companyItems.length > MAX_COMPANY_ITEMS_DISPLAYED

  return (
    <Layout hasTopPadding colorBlock={LAYOUT_COLOR_BLOCK.WHITE} data-testid="company_information_ecosystem">
      <Layout.Column>
        <Heading
          className={styles.ecosystem__title}
          data-testid="company_information_comany_ecosystem_title"
          as="h2"
          variant="title2"
          weight="bold"
        >
          {t("companies_client.information.companyEcosystem.title", { name })}
        </Heading>
        <div className={styles.ecosystem}>
          <div className={classNames(styles.ecosystem__wrapper, styles["ecosystem__wrapper--companyParent"])}>
            <Heading className={styles.ecosystem__wrapperTitle} as="h3" variant="title3" weight="bold">
              {t("companies_client.information.companyEcosystem.subtitleCompanyParent")}
            </Heading>
            <ul className={styles.ecosystem__content} data-testid="company_information_ecosystem_company_parent">
              <li>
                <CompanyItem {...companyParent} />
              </li>
            </ul>
          </div>
          <div className={styles.ecosystem__wrapper}>
            <Heading className={styles.ecosystem__wrapperTitle} as="h3" variant="title3" weight="bold">
              {t("companies_client.information.companyEcosystem.subtitleCompanyItems")}
            </Heading>
            <ul
              className={classNames([
                styles.ecosystem__content,
                styles["ecosystem__content--companyItems"],
                { [styles["ecosystem__content--limited"]]: !isDisplayAllCompanies },
              ])}
              data-testid="company_information_ecosystem_company_items"
            >
              {companyItems.map(companyItem => (
                <li key={`${companyItem.logoUrl}_${companyItem.name}`}>
                  <CompanyItem {...companyItem} />
                </li>
              ))}
            </ul>
            {hasMoreCompanyItems && (
              <Button variant={BUTTON_VARIANT.GHOST} minor onClick={displayMore} className={styles.ecosystem__showMore}>
                {isDisplayAllCompanies
                  ? t("companies_client.information.companyEcosystem.showLess")
                  : t("companies_client.information.companyEcosystem.showMore")}
              </Button>
            )}
          </div>
        </div>
      </Layout.Column>
    </Layout>
  )
}
